import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { Dropdown, SingleDatePicker, SubmitButton, TextInput, RadioButtonChip } from '@components/common';
import { otherInfoInitialValues, siteSubStageList, otherContractorTypeList, IPhoneValidationData } from '.';
import { useValidation } from '@hooks/validations';
import { formatDate, keyDownEvent, onImageError } from '@helper/utils';
import { ProductImages } from '@components/siteLeads/components/siteProducts/productImages';
import { AccordianDetailList } from '@components/endUserDetails/components/moreDetailsForm/accordianDetailList';
import { usePresignedURLQuery } from '@services/hooks/enduser-details/usePresignedURLQuery';
import {
	IAddNewSiteUpcomingPayload,
	useAddEditNewSiteMutation,
	IContractorDetailsResponse,
} from '@services/hooks/lms/addNewSite';
import { useImageUpload } from '@hooks/useImageUpload';
import { ToastType, useTosterStore } from '@store/toster';
import { useAddNewSiteStore } from '@store/lms/addNewSiteStore';
import { useTabNavigationGuard } from '@hooks/useTabNavigationGuard';
import { useNavigate } from 'react-router-dom';
import { useSiteContext } from '@components/lms/addNewSite';
import { useSetMainHeader } from '@hooks/useSetMainHeader';
import { useSiteListQuery } from '@services/hooks/lms/siteList';
import { useSiteAllDetailsStore } from '@store/siteAllDetailsStore';
import { useUploadSiteImagesMutation } from '@services/hooks/site-leads/useUploadSiteImagesMutation';
import { Modal } from '@components/common/modal/modal';
import { ContractorDetailsModal } from '@components/lms/common/contractorDetailsModal/contractorDetailsModal';
import { useContractorDetailsQuery } from '@services/hooks/lms/addNewSite/useContractorDetailsQuery';
import { usePhoneNumberValidateQuery } from '@services/hooks/enduser-details/usePhoneNumberValidateQuery';
import { ContractorTypeLabel } from '@components/lms/addNewSite/components/meetingDetails';

export const OtherInformation = (): ReactElement => {
	useTabNavigationGuard(1);
	const navigate = useNavigate();

	const { setToaster } = useTosterStore();
	const { setSubKeyValue, resetAddNewSiteValues, setKeyBaseValue } = useAddNewSiteStore();
	const { LmsOtherInfoValidationSchema } = useValidation();
	const { handleAddImage } = useImageUpload();
	const { siteData, isEditing } = useSiteContext();
	const { clearSiteDetails } = useSiteAllDetailsStore();
	useSetMainHeader(isEditing ? 'Site Details' : 'Add New Site');

	const { mutate: fetchContractorDetails } = useContractorDetailsQuery();
	const [isContractorModalOpen, setIsContractorModalOpen] = useState(false);
	const [contractorDetailsResult, setContractorDetailsResult] = useState<IContractorDetailsResponse | null>(null);
	const [phoneValidationData, setPhoneValidationData] = useState<IPhoneValidationData | null>(null);

	// Clear site details store when component unmounts for new sites
	useEffect(() => {
		return (): void => {
			if (!isEditing) {
				clearSiteDetails();
			}
		};
	}, [isEditing, clearSiteDetails]);

	const { mutate: mutatePresignedURL, data: getSiteImagesPresignedUrl } = usePresignedURLQuery();
	const { mutate: addNewSiteMutation } = useAddEditNewSiteMutation();
	const { refetch: refetchLmsSiteList } = useSiteListQuery();
	const { mutate: putSiteImagesData, isPending: isPutSiteImagePending } =
		useUploadSiteImagesMutation(getSiteImagesPresignedUrl);

	const [uploadedImageUrls, setUploadedImageUrls] = useState<string[]>([]);

	const formik = useFormik({
		initialValues: otherInfoInitialValues,
		validationSchema: LmsOtherInfoValidationSchema,
		onSubmit: () => {
			handleSubmitForm();
		},
	});

	const { refetch: refetchPhoneValidation } = usePhoneNumberValidateQuery(
		formik.values.contractorPhoneNumber,
		'contractorPhoneNumber'
	);

	const handleImageAdd = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const maxImageLimit = 5;
			const folderName = 'sitelead';
			const maximumFileSize = 20 * 1024 * 1024;
			const result = handleAddImage(event, formik.values.productImages, maximumFileSize, maxImageLimit, folderName);
			if (result) {
				const { payload, updatedImages } = result;
				updatedImages && formik.setFieldValue('productImages', updatedImages);
				if (payload) {
					mutatePresignedURL(payload, {
						onSuccess: (presignedUrls) => {
							setUploadedImageUrls(presignedUrls.map((img) => img.normal_url));
						},
					});
				}
			}
		},
		[formik, handleAddImage, mutatePresignedURL]
	);

	const handlePrimaryNumberChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
		let val = e.target.value.replace(/\D/g, '');
		val = val.slice(0, 10);
		formik.setFieldValue('contractorPhoneNumber', val);
	};

	const handleContractorTypeChange = useCallback(
		(val: string) => {
			formik.setFieldValue('contractorType', val);

			if (val === ContractorTypeLabel.NONE) {
				// Clear contractor details when NONE is selected
				formik.setFieldValue('contractorName', '');
				formik.setFieldValue('contractorPhoneNumber', '');
			}

			// Trigger validation after field changes
			setTimeout(() => {
				formik.validateForm();
			}, 0);
		},
		[formik]
	);

	const handleContractorModalOpen = async (): Promise<void> => {
		if (!formik.values.contractorPhoneNumber || formik.values.contractorPhoneNumber.length !== 10) {
			setToaster(true, ToastType.error, 'Please enter a valid 10-digit phone number');
			return;
		}

		try {
			if (!refetchPhoneValidation) {
				setToaster(true, ToastType.error, 'Phone validation service unavailable');
				return;
			}

			const response = await refetchPhoneValidation();
			const validationData = response.data;
			setPhoneValidationData((validationData as IPhoneValidationData) || null);

			if (validationData?.data?.exists) {
				fetchContractorDetails(
					{ phone: formik.values.contractorPhoneNumber },
					{
						onSuccess: (contractorDetails: IContractorDetailsResponse) => {
							if (!contractorDetails) {
								return;
							}

							setContractorDetailsResult(contractorDetails);
							setKeyBaseValue('contractorName', contractorDetails.name ?? '');
							setKeyBaseValue('membershipNo', contractorDetails.membershipno ?? '');
							formik.setFieldValue('contractorName', contractorDetails.name ?? '');

							if (contractorDetails.status === 'lead') {
								setKeyBaseValue('appIdC', contractorDetails.app_id_c ?? '');
							}
							if (contractorDetails.status === 'account') {
								setKeyBaseValue('endUserC', contractorDetails.sfid ?? '');
							}
						},
						onError: (error) => {
							setToaster(true, ToastType.error, error?.message ?? 'Failed to fetch contractor details');
						},
					}
				);
			}

			// Open the modal
			setIsContractorModalOpen(true);
		} catch (error) {
			setToaster(true, ToastType.error, 'Failed to validate phone number');
		}
	};

	const handleAddNewSiteApi = useCallback(() => {
		const addNewSiteStore = useAddNewSiteStore.getState();

		const visitImageFields: { [key: string]: string | null } = {};
		for (let i = 0; i < 5; i++) {
			visitImageFields[`visit_image${i + 1}`] = uploadedImageUrls[i] || null;
		}

		const payload: IAddNewSiteUpcomingPayload = {
			site_lead: {
				/* site info fields */
				name: addNewSiteStore.siteInfo.siteName,
				site_location_c: addNewSiteStore.siteInfo.siteLocation,
				address_c: addNewSiteStore.siteInfo.siteAddress,
				latitude: addNewSiteStore.siteInfo.latitude,
				longitude: addNewSiteStore.siteInfo.longitude,
				pincode_c: addNewSiteStore.siteInfo.pincode,
				town_name_c: addNewSiteStore.siteInfo.city,
				site_sub_type_c: addNewSiteStore.siteInfo.siteType,
				type_of_property: addNewSiteStore.siteInfo.typeOfProperty,
				application_area_c: addNewSiteStore.siteInfo.propertyArea,
				site_current_status_c: addNewSiteStore.siteInfo.siteStage,

				/* other info fields */
				site_sub_stage: addNewSiteStore.otherInfo.siteSubStage,
				date_of_visit: addNewSiteStore.otherInfo.tentativeNextVisit
					? formatDate(new Date(addNewSiteStore.otherInfo.tentativeNextVisit))
					: '',
				glue_potential_in_kg: addNewSiteStore.otherInfo.gluePotentialOfSite,
				whom_did_you_meet_c: addNewSiteStore.otherInfo.contractorType,
				supervisor_name: addNewSiteStore.otherInfo.contractorName,
				supervisor_phone_number: addNewSiteStore.otherInfo.contractorPhoneNumber,
				home_owner_name_c: addNewSiteStore.otherInfo.homeOwnerDetails[0]?.name,
				home_owner_mobile_number_c: addNewSiteStore.otherInfo.homeOwnerDetails[0].number,
				...(addNewSiteStore.otherInfo.contractorType === ContractorTypeLabel.Contractor
					? {
							type_of_contracts: addNewSiteStore.typeOfContracts.replace(/\s+/g, '_'),
							no_of_sites: addNewSiteStore.noOfSites,
							contractor_potential_per_month: addNewSiteStore.contractorPotentialPerMonth,
							lead_app_id_c: addNewSiteStore.appIdC,
							end_user_c: addNewSiteStore.endUserC,
							contractor_name: addNewSiteStore.otherInfo.contractorName,
							contractor_phone_number: addNewSiteStore.otherInfo.contractorPhoneNumber,
						}
					: {}),
				is_bathroom: addNewSiteStore.siteInfo.is_bathroom,
				is_other_rooms: addNewSiteStore.siteInfo.is_other_rooms,
				bathroom_work_status: addNewSiteStore.siteInfo.bathroom_work_status,
				other_rooms_work_status: addNewSiteStore.siteInfo.other_rooms_work_status,
				tiling_contractor_name: addNewSiteStore.otherInfo.tilingContractorDetails[0]?.name,
				tiling_contractor_phone: addNewSiteStore.otherInfo.tilingContractorDetails[0]?.number,
				avg_monthly_tile_adhesive_consumption: addNewSiteStore.otherInfo.tilingContractorDetails[0]
					?.avg_monthly_tile_adhesive_consumption
					? Number(addNewSiteStore.otherInfo.tilingContractorDetails[0].avg_monthly_tile_adhesive_consumption)
					: 0,
			},
			visit: {
				...visitImageFields,
			},
		};

		addNewSiteMutation(payload, {
			onSuccess: () => {
				setToaster(true, ToastType.success, 'Site lead added successfully!');
				resetAddNewSiteValues();
				refetchLmsSiteList?.();
				navigate('/');
			},
			onError: (error: Error) => {
				setToaster(true, ToastType.error, error.message);
			},
		});
	}, [addNewSiteMutation, navigate, resetAddNewSiteValues, setToaster, uploadedImageUrls, refetchLmsSiteList]);

	const updateSiteImages = useCallback((): void => {
		putSiteImagesData(formik.values.productImages, {
			onSuccess: () => {
				handleAddNewSiteApi();
			},
		});
	}, [formik.values.productImages, putSiteImagesData, handleAddNewSiteApi]);

	const handleSubmitForm = useCallback(() => {
		const store = useAddNewSiteStore.getState();

		// Only require contractor details if contractor type is Contractor (not NONE)
		if (
			formik.values.contractorType === ContractorTypeLabel.Contractor &&
			(!store.typeOfContracts || !store.noOfSites || !store.contractorPotentialPerMonth)
		) {
			setToaster(true, ToastType.error, 'Contractor details are required');
			return;
		}
		const keys = [
			'siteSubStage',
			'tentativeNextVisit',
			'gluePotentialOfSite',
			'contractorType',
			'contractorName',
			'contractorPhoneNumber',
			'homeOwnerDetails',
			'tilingContractorDetails',
		] as const;

		keys.forEach((key) => {
			setSubKeyValue('otherInfo', key, formik.values[key]);
		});

		// First upload images, then proceed with site creation
		if (formik.values.productImages.length > 0) {
			updateSiteImages();
		} else {
			handleAddNewSiteApi();
		}
	}, [formik.values, handleAddNewSiteApi, setSubKeyValue, updateSiteImages, setToaster]);

	useEffect(() => {
		const store = useAddNewSiteStore.getState();
		if (!formik.values.siteSubStage && !formik.values.tentativeNextVisit) {
			const initialValues = {
				siteSubStage: store.otherInfo.siteSubStage,
				tentativeNextVisit: store.otherInfo.tentativeNextVisit,
				gluePotentialOfSite: store.otherInfo.gluePotentialOfSite,
				contractorType: store.otherInfo.contractorType,
				contractorName: store.otherInfo.contractorName,
				contractorPhoneNumber: store.otherInfo.contractorPhoneNumber,
				homeOwnerDetails: store.otherInfo.homeOwnerDetails,
				tilingContractorDetails: store.otherInfo.tilingContractorDetails ?? [],
				productImages: [],
			};
			formik.setValues(initialValues);
		}

		// eslint-disable-next-line
	}, []);

	useEffect(() => {
		if (isEditing && siteData) {
			formik.setValues({
				siteSubStage: siteData.site_sub_stage,
				tentativeNextVisit: siteData.date_of_visit ? new Date(siteData.date_of_visit) : '',
				gluePotentialOfSite: siteData.glue_potential_in_kg,
				contractorType: siteData.whom_did_you_meet_c,
				contractorName: siteData.supervisor_name,
				contractorPhoneNumber: siteData.supervisor_phone_number,
				homeOwnerDetails: [
					{
						id: 0,
						isActive: true,
						name: siteData.home_owner_name_c,
						number: siteData.home_owner_mobile_number_c,
					},
				],
				productImages: [],
				tilingContractorDetails: [
					{
						id: 0,
						isActive: true,
						name: siteData?.tiling_contractor_name,
						number: siteData?.tiling_contractor_phone,
						avg_monthly_tile_adhesive_consumption: siteData?.avg_monthly_tile_adhesive_consumption,
					},
				],
			});
			if (
				siteData.visit.visit_image1 ||
				siteData.visit.visit_image2 ||
				siteData.visit.visit_image3 ||
				siteData.visit.visit_image4 ||
				siteData.visit.visit_image5
			) {
				const siteDataImages = [
					siteData.visit.visit_image1,
					siteData.visit.visit_image2,
					siteData.visit.visit_image3,
					siteData.visit.visit_image4,
					siteData.visit.visit_image5,
				].filter(Boolean);

				setUploadedImageUrls(siteDataImages);
			}
		}

		// eslint-disable-next-line
	}, [siteData, isEditing]);

	// Contractor details editable only when not captured earlier (LMS: re-visit with blank = can add; once captured = not editable)
	const contractorDetailsPreviouslyCaptured = useMemo(
		() => !!(siteData?.supervisor_name?.trim() && siteData?.supervisor_phone_number?.trim()),
		[siteData?.supervisor_name, siteData?.supervisor_phone_number]
	);

	const isContractorFieldsDisabled = isEditing && contractorDetailsPreviouslyCaptured;

	return (
		<div className="add-new-site-form">
			<form onSubmit={formik.handleSubmit}>
				<ul className="form-wrapper row">
					<li className="d-col d-col-2">
						<Dropdown
							label="Site Sub-Stage"
							id="siteSubStage"
							name="siteSubStage"
							onBlur={formik.handleBlur}
							title={formik.values.siteSubStage ?? ''}
							error={formik.touched.siteSubStage && formik.errors.siteSubStage ? formik.errors.siteSubStage : null}
							options={siteSubStageList}
							setFieldValue={formik.setFieldValue}
							required
							disabled={isEditing}
						/>
					</li>
					<li className="d-col d-col-2">
						<SingleDatePicker
							label="Tentative Woodworking Start Date"
							id="tentativeNextVisit"
							name="tentativeNextVisit"
							className="single-date-picker"
							value={formik.values.tentativeNextVisit}
							formik={formik}
							minDate={new Date()}
							error={
								formik.touched.tentativeNextVisit && formik.errors.tentativeNextVisit
									? formik.errors.tentativeNextVisit
									: null
							}
							required
							disabled={isEditing}
						/>
					</li>
					<li className="form-control d-col d-col-2">
						<TextInput
							type="text"
							label="Glue Potential of the site in KG"
							id="gluePotentialOfSite"
							name="gluePotentialOfSite"
							onBlur={formik.handleBlur}
							value={formik.values.gluePotentialOfSite ?? ''}
							setFieldValue={formik.setFieldValue}
							onKeyDown={keyDownEvent}
							error={
								formik.touched.gluePotentialOfSite && formik.errors.gluePotentialOfSite
									? formik.errors.gluePotentialOfSite
									: null
							}
							required
							disabled={isEditing}
						/>
					</li>
					<li className="d-col d-col-2">
						<ProductImages
							fieldName="productImages"
							formik={formik}
							handleAddImage={handleImageAdd}
							required
							showAddButton={!isEditing}
						/>
					</li>
					{isEditing && uploadedImageUrls.length > 0 && (
						<li className="d-col d-col-2 site-product-images-wrapper">
							<div className="upload-images-wrapper">
								{uploadedImageUrls?.map((image: string) => (
									<div key={`${image}`} className="upload-image">
										<div className="wrapper-upload-images">
											<img
												role="presentation"
												src={image}
												alt={image}
												title="site"
												width="56"
												height="56"
												onError={onImageError}
											/>
										</div>
									</div>
								))}
							</div>
						</li>
					)}
					<li className="d-col">
						<RadioButtonChip
							label="Who did you meet at site?"
							options={otherContractorTypeList}
							value={formik.values.contractorType ?? ''}
							onChange={handleContractorTypeChange}
							onBlur={() => formik.setFieldTouched('contractorType', true)}
							error={
								formik.touched.contractorType && formik.errors.contractorType ? formik.errors.contractorType : null
							}
							required
							disabled={isEditing}
						/>
					</li>
					{formik.values.contractorType !== ContractorTypeLabel.NONE && (
						<>
							<li className="form-control d-col d-col-2">
								<TextInput
									label="Name"
									type="text"
									id="contractorName"
									name="contractorName"
									value={formik.values.contractorName ?? ''}
									setFieldValue={formik.setFieldValue}
									onBlur={formik.handleBlur}
									error={
										formik.touched.contractorName && formik.errors.contractorName ? formik.errors.contractorName : null
									}
									required
									disabled={isContractorFieldsDisabled}
								/>
							</li>
							<li className="form-control d-col d-col-2">
								<TextInput
									label="Phone Number"
									type="tel"
									id="contractorPhoneNumber"
									name="contractorPhoneNumber"
									onBlur={formik.handleBlur}
									value={formik.values.contractorPhoneNumber ?? ''}
									setFieldValue={formik.setFieldValue}
									error={
										formik.touched.contractorPhoneNumber && formik.errors.contractorPhoneNumber
											? formik.errors.contractorPhoneNumber
											: null
									}
									onKeyDown={keyDownEvent}
									maxLength={10}
									onChange={handlePrimaryNumberChange}
									required
									disabled={isContractorFieldsDisabled}
								/>
							</li>

							{ContractorTypeLabel.Contractor === formik.values.contractorType &&
								(!isEditing || !contractorDetailsPreviouslyCaptured) && (
									<li className="d-col contractor-submit-wrapper">
										<div className="contractor-status">
											<span className="icon icon-info"></span>
											<h5 className="text">Click Submit to check contractor enrollment status.</h5>
										</div>
										<button
											type="button"
											aria-label="submit contractor"
											className="rounded-btn primary-outline"
											disabled={
												!formik.values.contractorPhoneNumber ||
												formik.values.contractorPhoneNumber.length !== 10 ||
												!formik.values.contractorName ||
												Boolean(formik.errors.contractorName) ||
												Boolean(formik.errors.contractorPhoneNumber)
											}
											onClick={handleContractorModalOpen}
										>
											Submit
										</button>
									</li>
								)}
						</>
					)}
					{formik.values.homeOwnerDetails.map((owner, index) => (
						<AccordianDetailList
							key={owner.id}
							title="Home Owner"
							accordianName="homeOwnerDetails"
							accordianListIndex={index}
							formik={formik}
							data={owner}
							isEditing={isEditing}
						/>
					))}
					{formik.values.tilingContractorDetails.map((tilingContractor, index) => (
						<AccordianDetailList
							key={tilingContractor.id}
							title="Tiling Contractor"
							accordianName="tilingContractorDetails"
							accordianListIndex={index}
							formik={formik}
							data={tilingContractor}
							isEditing={isEditing}
							isTilingContractorDetail={true}
						/>
					))}
				</ul>
				<SubmitButton
					label="Submit"
					isDisabled={!formik.isValid || !formik.dirty || !formik.values.productImages.length}
					handleSubmitClick={formik.handleSubmit}
					isPending={isPutSiteImagePending}
				/>
			</form>

			<Modal
				modalTitle={`Contractor Details`}
				modalOpen={isContractorModalOpen}
				modalId="contractorDetailsModal"
				isEscAllow={false}
				className="dialog-lg dialog-bottom dialog-conform"
			>
				<ContractorDetailsModal
					isContractorExists={phoneValidationData?.data?.exists ?? false}
					name={formik.values.contractorName ?? ''}
					phone={formik.values.contractorPhoneNumber ?? ''}
					contractorDetails={contractorDetailsResult || null}
					onCancel={() => setIsContractorModalOpen(false)}
				/>
			</Modal>
		</div>
	);
};
